import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import { useCart } from '../../context/CartContext';
import {
  Avatar, Badge, Button, Empty, Input, Loading, Rating, SectionHeader, Stepper,
} from '../../components/Ui';
import { ProductCard } from '../../components/Cards';
import { Reveal } from '../../components/Motion';
import Gallery from '../../components/Gallery';
import PageHeader from '../../components/PageHeader';
import { colors } from '../../theme';
import { currency, discountPercent } from '../../utils/format';
import { useToast } from '../../context/ToastContext';

const ProductDetailPage = () => {
  // Opened from the partner panel with ?preview=1: same page, ordering switched off.
  const preview = new URLSearchParams(useLocation().search).get('preview') === '1';
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    addItem, updateItem, quantityOf, itemCount,
  } = useCart();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /*
   * A dish is customised before it goes in the cart: size, extras, how many,
   * a note. A cart line passes its choices back through location state.
   */
  const routeState = useLocation().state;
  const preset = (routeState && routeState.preset) || null;
  const [variant, setVariant] = useState((preset && preset.variantLabel) || '');
  const [picked, setPicked] = useState((preset && preset.addons) || []);
  const [qty, setQty] = useState((preset && preset.quantity) || 1);
  const [note, setNote] = useState((preset && preset.note) || '');

  useEffect(() => {
    setLoading(true);
    api.products.detail(id)
      .then((res) => {
        setData(res.data);
        const p = res.data && res.data.product;
        if (p && p.variants && p.variants.length && !(preset && preset.variantLabel)) setVariant(p.variants[0].label);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id, preset]);

  if (loading) return <Loading text="Loading product…" />;
  if (!data) {
    return <Empty emoji="📦" title="Product not found" actionLabel="Go back" onAction={() => navigate(-1)} />;
  }

  const { product, related } = data;
  const inCart = quantityOf(product._id);
  const off = discountPercent(product.mrp, product.price);
  const images = product.images || [];

  const variants = product.variants || [];
  const groups = product.addonGroups || [];
  const isDish = variants.length > 0 || groups.length > 0 || Boolean(product.menuCategory);
  const chosenVariant = variants.find((v) => v.label === variant) || variants[0];
  const unitPrice = (chosenVariant ? chosenVariant.price : product.price) + picked.reduce((n, a) => n + a.price, 0);
  const missingGroup = groups.find((g) => g.required && !picked.some((a) => a.group === g.title));
  const foodTone = product.foodType === 'veg' ? 'var(--success)' : product.foodType === 'egg' ? '#D97706' : 'var(--danger)';

  const toggleAddon = (group, option) => {
    setPicked((list) => {
      const inGroup = list.filter((a) => a.group === group.title);
      const already = inGroup.some((a) => a.name === option.name);
      const others = list.filter((a) => a.group !== group.title);
      if (already) return [...others, ...inGroup.filter((a) => a.name !== option.name)];
      const entry = { group: group.title, name: option.name, price: option.price };
      return group.multi ? [...others, ...inGroup, entry] : [...others, entry];
    });
  };

  const addDish = async () => {
    if (missingGroup) { toast.error(`Please choose ${missingGroup.title}.`, 'One more choice'); return; }
    setBusy(true);
    try {
      if (preset && preset.lineKey) await updateItem(product._id, 0, preset.lineKey);
      await addItem(product._id, qty, {
        variantLabel: chosenVariant ? chosenVariant.label : '',
        addons: picked.map((a) => ({ group: a.group, name: a.name })),
        note,
      });
      navigate('/cart');
    } catch (e) {
      // the cart context already toasts
    } finally { setBusy(false); }
  };

  const changeQty = async (next) => {
    setBusy(true);
    try {
      if (inCart === 0) await addItem(product._id, 1);
      else await updateItem(product._id, next);
    } catch (e) {
      // Stock errors surface on the cart page.
    } finally { setBusy(false); }
  };

  return (
    <div className="has-action-bar">
      <PageHeader title={product.title} subtitle={(product.partner && product.partner.businessName) || ''} />

      <div className="split wide-side">
        <div>
          <Gallery
            images={images}
            contain
            fallback={(product.category && product.category.icon) || '📦'}
            emptyLabel="No photo for this product yet"
            badge={off > 0 ? (
              <span
                style={{
                  position: 'absolute', top: 12, left: 12, background: colors.accent,
                  color: '#fff', padding: '4px 10px', borderRadius: 8, fontWeight: 800, fontSize: 11.5,
                }}
              >
                {off}% OFF
              </span>
            ) : null}
          />
          {product.video ? (
            <div style={{ marginTop: 14 }}>
              <div className="t-small" style={{ fontWeight: 700, marginBottom: 6 }}>🎬 Video</div>
              <video src={fileUrl(product.video)} controls style={{ width: '100%', maxHeight: 420, borderRadius: 14, background: '#000' }} />
            </div>
          ) : null}

          <Reveal style={{ marginTop: 16 }}>
            <div className="t-small">
              {product.unit}{product.brand ? ` · ${product.brand}` : ''}
            </div>
            {isDish ? (
        <div className="card sh-sm" style={{ marginBottom: 16 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span className="veg-mark" style={{ borderColor: foodTone }}><span style={{ background: foodTone }} /></span>
            <span className="t-small">
              {product.foodType === 'veg' ? 'Veg' : product.foodType === 'egg' ? 'Contains egg' : 'Non-veg'}
              {product.menuCategory ? ` · ${product.menuCategory}` : ''}
            </span>
          </div>

          {variants.length ? (
            <>
              <div className="t-title" style={{ marginTop: 14 }}>Choose size</div>
              {variants.map((v) => (
                <button type="button" key={v.label} className={`option-row ${chosenVariant && chosenVariant.label === v.label ? 'on' : ''}`} onClick={() => setVariant(v.label)}>
                  <span>{chosenVariant && chosenVariant.label === v.label ? '🔘' : '⚪️'}</span>
                  <span className="grow">{v.label}</span>
                  <strong>{currency(v.price)}</strong>
                </button>
              ))}
            </>
          ) : null}

          {groups.map((g) => (
            <div key={g.title}>
              <div className="t-title" style={{ marginTop: 14 }}>
                {g.title} <span className="t-tiny">{g.required ? '· required' : '· optional'}{g.multi ? '' : ' · pick one'}</span>
              </div>
              {g.options.map((o) => {
                const on = picked.some((a) => a.group === g.title && a.name === o.name);
                return (
                  <button type="button" key={o.name} className={`option-row ${on ? 'on' : ''}`} onClick={() => toggleAddon(g, o)}>
                    <span>{on ? (g.multi ? '☑️' : '🔘') : (g.multi ? '⬜️' : '⚪️')}</span>
                    <span className="grow">{o.name}</span>
                    <strong>{o.price ? `+ ${currency(o.price)}` : 'Free'}</strong>
                  </button>
                );
              })}
            </div>
          ))}

          <Input
            label="Cooking instructions (optional)"
            placeholder="Less oil, no onion…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ marginTop: 14, marginBottom: 0 }}
          />
        </div>
      ) : null}

      {product.description ? (
              <>
                <h3 className="h3" style={{ marginTop: 18 }}>Description</h3>
                <p className="t-body" style={{ marginTop: 8, lineHeight: 1.7 }}>{product.description}</p>
              </>
            ) : null}

            {(() => {
              // Facts the partner filled in: brand, model, warranty, then their own fields (tyre size...).
              const facts = [
                ['Brand', product.brand], ['Model', product.modelNumber], ['Warranty', product.warranty],
                ['Installation', product.installationAvailable ? 'Available' : ''],
                ['Delivery', product.deliveryAvailable ? 'Available' : ''],
                // What the category asks about - size, colour, who it is for.
                ...((product.attributes || []).map((a) => [a.label, a.values.map((v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : v)).join(', ')])),
                ...((product.specs || []).map((s) => [s.key, s.value])),
              ].filter(([, v]) => v);
              if (!facts.length) return null;
              return (
                <>
                  <h3 className="h3" style={{ marginTop: 18 }}>Details</h3>
                  <div className="card" style={{ marginTop: 8, padding: 0, overflow: 'hidden' }}>
                    {facts.map(([k, v], i) => (
                      <div key={`${k}-${i}`} className="row-between" style={{ padding: '10px 14px', borderTop: i ? '1px solid var(--line)' : 'none', gap: 12 }}>
                        <span className="t-small" style={{ color: 'var(--ink-2)' }}>{k}</span>
                        <span className="t-small" style={{ fontWeight: 600, textAlign: 'right' }}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </Reveal>
        </div>

        <div className="split-side">
          {preview ? (
          <div className="action-bar" style={{ background: 'var(--warning-bg)' }}>
            <div className="container row-between" style={{ gap: 12 }}>
              <div>
                <strong style={{ color: 'var(--warning)' }}>👀 Customer preview</strong>
                <div className="t-tiny">This is how customers see it. Ordering is off in preview.</div>
              </div>
              <Button title="Back to partner panel" variant="light" onClick={() => navigate(-1)} />
            </div>
          </div>
          ) : (
          <div className="action-bar">
            <div className="action-main">
              <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 22, fontWeight: 800 }}>{currency(product.price)}</span>
                {off > 0 ? (
                  <span style={{ fontSize: 13, color: colors.faint, textDecoration: 'line-through' }}>
                    {currency(product.mrp)}
                  </span>
                ) : null}
              </div>
              <div className="action-extra" style={{ marginTop: 6 }}>
                <Rating value={product.rating} count={product.ratingCount} size={13} />
                <div style={{ marginTop: 10 }}>
                  <Badge
                    value={product.inStock ? `In stock (${product.stock})` : 'Out of stock'}
                    color={product.inStock ? colors.success : colors.danger}
                  />
                </div>
              </div>
            </div>

            <div className="action-cta">
              {isDish ? (
                <div className="row" style={{ gap: 12 }}>
                  <Stepper value={qty} onChange={(q) => setQty(Math.min(20, Math.max(1, q)))} min={1} />
                  <Button
                    title={`${preset && preset.lineKey ? 'Update' : 'Add item'} · ${currency(unitPrice * qty)}`}
                    full
                    loading={busy}
                    disabled={!product.inStock}
                    onClick={addDish}
                  />
                </div>
              ) : inCart > 0 ? (
                <div className="row-between" style={{ gap: 12 }}>
                  <span className="t-small">In cart</span>
                  <Stepper value={inCart} onChange={changeQty} disabled={busy} />
                </div>
              ) : (
                <Button
                  title="Add to cart"
                  icon="🛒"
                  full
                  loading={busy}
                  disabled={!product.inStock}
                  onClick={() => changeQty(1)}
                />
              )}
            </div>

            {itemCount > 0 ? (
              <div className="action-extra" style={{ marginTop: 10 }}>
                <Button
                  title={`View cart · ${itemCount} item${itemCount > 1 ? 's' : ''}`}
                  variant="light"
                  full
                  onClick={() => navigate('/cart')}
                />
              </div>
            ) : null}
          </div>
          )}

          {product.partner ? (
            <div className="action-extra">
              <h3 className="h3" style={{ marginTop: 22, marginBottom: 10 }}>Sold by</h3>
              <button
                type="button"
                onClick={() => navigate(`/business/${product.partner._id}`)}
                className="card sh-sm press hoverable"
                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left' }}
              >
                <Avatar name={product.partner.businessName} uri={fileUrl(product.partner.logo)} size={46} />
                <span className="grow">
                  <span className="t-title" style={{ display: 'block' }}>{product.partner.businessName}</span>
                  <Rating value={product.partner.rating} count={product.partner.ratingCount} size={12} />
                </span>
                <span style={{ fontSize: 20, color: colors.faint }}>›</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {related && related.length ? (
        <>
          <SectionHeader title="You may also like" />
          <div className="rail">
            {related.map((p) => (
              <ProductCard
                key={p._id}
                product={p}
                width={168}
                inCart={quantityOf(p._id)}
                onAdd={() => addItem(p._id, 1).catch(() => {})}
                onClick={() => navigate(`/product/${p._id}`)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ProductDetailPage;
